import { MapPin, Package, Settings, FileText, ChevronRight } from 'lucide-react'

interface SearchResult {
  id: string
  title: string
  content: string
  type: 'page' | 'package' | 'service' | 'blog'
  url: string
  section?: string
  score?: number
}

interface SearchResultsProps {
  results: SearchResult[]
  query: string
  onResultClick: (result: SearchResult) => void
  isVisible: boolean
}

const getResultIcon = (type: string) => {
  switch (type) {
    case 'page':
      return <FileText className="h-4 w-4 text-blue-600" />
    case 'package':
      return <Package className="h-4 w-4 text-green-600" />
    case 'service':
      return <Settings className="h-4 w-4 text-purple-600" />
    case 'blog':
      return <FileText className="h-4 w-4 text-orange-600" />
    default:
      return <MapPin className="h-4 w-4 text-gray-600" />
  }
}

const getResultTypeLabel = (type: string) => {
  switch (type) {
    case 'page':
      return 'Page'
    case 'package':
      return 'Travel Package'
    case 'service':
      return 'Service'
    case 'blog':
      return 'Blog Post'
    default:
      return 'Content'
  }
}

export default function SearchResults({ results, query, onResultClick, isVisible }: SearchResultsProps) {
  if (!isVisible || !query.trim()) return null

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-2xl border border-gray-200 z-[100] max-h-96 overflow-y-auto">
      {results.length === 0 ? (
        <div className="p-4 text-center text-gray-500">
          <div className="mb-2">
            <MapPin className="h-8 w-8 text-gray-300 mx-auto" />
          </div>
          <p className="text-sm">No results found for "{query}"</p>
          <p className="text-xs text-gray-400 mt-1">Try searching for destinations, services, or travel packages</p>
        </div>
      ) : (
        <>
          <div className="p-3 border-b border-gray-100">
            <p className="text-xs text-gray-500 font-medium">
              {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
            </p>
          </div>
          <div className="py-2">
            {results.map((result) => (
              <button
                key={result.id}
                onClick={() => onResultClick(result)}
                className="w-full px-4 py-3 hover:bg-gray-50 text-left transition-colors duration-150 flex items-start space-x-3 group"
              >
                <div className="flex-shrink-0 mt-0.5">
                  {getResultIcon(result.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                      {result.title}
                    </h4>
                    <ChevronRight className="h-3 w-3 text-gray-400 flex-shrink-0 ml-2 group-hover:text-blue-600 transition-colors" />
                  </div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {getResultTypeLabel(result.type)}
                    </span>
                    {result.section && (
                      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                        {result.section}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2">
                    {result.content.length > 100
                      ? `${result.content.substring(0, 100)}...`
                      : result.content
                    }
                  </p>
                </div>
              </button>
            ))}
          </div>
          <div className="p-3 border-t border-gray-100 bg-gray-50 text-center">
            <p className="text-xs text-gray-500">
              Press <kbd className="px-1.5 py-0.5 text-xs font-semibold text-gray-800 bg-gray-200 rounded">Enter</kbd> to visit the first result
            </p>
          </div>
        </>
      )}
    </div>
  )
}